/**
 * <scent-explorer> — tab strip + active panel for the homepage scent library.
 *
 * Markup:
 *   [data-tab-scroll] wraps [role="tablist"] flex row with w-max (+ optional md:ml-auto).
 */

class ScentExplorer extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-scent]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"][data-panel]'));
    this.tabScroll = this.querySelector('[data-tab-scroll]');

    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#activate(tab.dataset.scent));
      tab.addEventListener('keydown', (e) => this.#onKeydown(e));
    });

    const scroller = this.tabScroll instanceof HTMLElement ? this.tabScroll : null;
    if (scroller) {
      this.#bindWheelHorizontal(scroller);
      this.#bindPointerDragScroll(scroller);
    }
  }

  /**
   * @param {HTMLElement} el
   */
  #needsHorizontalScroll(el) {
    return el.scrollWidth > el.clientWidth + 1;
  }

  /**
   * @param {HTMLElement} el
   */
  #bindWheelHorizontal(el) {
    el.addEventListener(
      'wheel',
      (e) => {
        if (!this.#needsHorizontalScroll(el)) return;

        let dx = e.deltaX;
        let dy = e.deltaY;
        /** Shift + vertical wheel → horizontal scroll in many browsers; normalize here too */
        if (e.shiftKey && dy !== 0 && Math.abs(dx) <= Math.abs(dy)) {
          dx = dy;
          dy = 0;
        }
        /** Map dominant vertical gesture to horizontal when there is nothing to scroll on X delta */
        if (Math.abs(dy) >= Math.abs(dx)) {
          dx = dy;
          dy = 0;
        }

        let px = dx;
        if (e.deltaMode === 1) {
          px *= 16;
        } else if (e.deltaMode === 2) {
          px *= el.clientWidth;
        }
        if (px === 0) return;

        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        let nextLeft = el.scrollLeft + px;
        if (nextLeft < 0) nextLeft = 0;
        if (nextLeft > maxScroll) nextLeft = maxScroll;

        if (nextLeft === el.scrollLeft) {
          /** At strip edge — let the page capture vertical scrolling */
          return;
        }

        el.scrollLeft = nextLeft;
        e.preventDefault();
      },
      { passive: false },
    );
  }

  /**
   * Horizontal drag-scroll (desktop mouse / pen). Touch keeps native strip scrolling.
   * @param {HTMLElement} el
   */
  #bindPointerDragScroll(el) {
    /** @type {number | null} */
    let activeId = null;
    let originX = 0;
    let originScroll = 0;
    let dragging = false;

    /** @param {PointerEvent} e */
    const onMove = (e) => {
      if (activeId === null || e.pointerId !== activeId) return;
      const dx = e.clientX - originX;

      if (!dragging && Math.abs(dx) > 8) {
        dragging = true;
        try {
          document.body.style.userSelect = 'none';
        } catch {
          //
        }
        el.classList.add('is-dragging-tabstrip');
      }

      if (dragging) {
        el.scrollLeft = originScroll - dx;
        e.preventDefault();
      }
    };

    /** @param {PointerEvent} e */
    const onEnd = (e) => {
      if (activeId === null || e.pointerId !== activeId) return;
      activeId = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);

      try {
        document.body.style.userSelect = '';
      } catch {
        //
      }
      el.classList.remove('is-dragging-tabstrip');

      const wasDrag = dragging;
      dragging = false;

      if (!wasDrag) return;

      const swallow = /** @param {MouseEvent} ev */ (ev) => {
        if (!el.contains(/** @type {Node | null} */ (ev.target))) return;
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();
      };
      /** Ghost click after drag would toggle the pill under cursor */
      document.addEventListener('click', swallow, { capture: true, once: true });
    };

    el.addEventListener(
      'pointerdown',
      (e) => {
        if (!this.#needsHorizontalScroll(el)) return;
        if (e.pointerType === 'touch') return;
        if (!(e.buttons & 1)) return;

        activeId = e.pointerId;
        originX = e.clientX;
        originScroll = el.scrollLeft;
        dragging = false;

        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onEnd);
        window.addEventListener('pointercancel', onEnd);
      },
      { passive: true },
    );
  }

  #activate(id) {
    let activeTab = null;
    this.tabs.forEach((t) => {
      const on = t.dataset.scent === id;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      t.classList.toggle('is-active', on);
      if (on) activeTab = t;
    });
    this.panels.forEach((p) => {
      const on = p.dataset.panel === id;
      p.toggleAttribute('hidden', !on);
      p.classList.toggle('is-active', on);
    });

    const scroller = this.tabScroll instanceof HTMLElement ? this.tabScroll : null;
    if (activeTab && scroller) {
      activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      requestAnimationFrame(() => {
        if (!scroller.isConnected || !activeTab.isConnected) return;
        activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }
  }

  /** @param {KeyboardEvent} e */
  #onKeydown(e) {
    const idx = this.tabs.indexOf(/** @type {Element} */ (e.target));
    if (idx === -1) return;
    let next = -1;
    switch (e.key) {
      case 'ArrowRight':
        next = (idx + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        next = (idx - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const tab = this.tabs[next];
    tab.focus();
    this.#activate(tab.dataset.scent);
  }
}

if (!customElements.get('scent-explorer')) {
  customElements.define('scent-explorer', ScentExplorer);
}
